import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthChange, logoutUser, handleRedirectResult } from '../services/firebase';
import { getAuth, browserLocalPersistence, setPersistence } from 'firebase/auth';

const AuthContext = createContext({
  currentUser: null,
  isLoading: true,
  userMessageCount: 0,
  incrementMessageCount: () => {},
  resetMessageCount: () => {},
  signOut: async () => {},
  bypassAuth: () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userMessageCount, setUserMessageCount] = useState(() => {
    return parseInt(localStorage.getItem('chatMessageCount') || '0', 10);
  });
  
  // Configure Firebase persistence
  useEffect(() => {
    const auth = getAuth();
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('Firebase persistence set to LOCAL');
      })
      .catch((error) => {
        console.error('Error setting auth persistence:', error);
      });
  }, []);

  // Check for redirect result when the component mounts
  useEffect(() => {
    async function checkRedirectResult() {
      try {
        console.log("Checking for redirect authentication result...");
        const user = await handleRedirectResult();
        if (user) {
          console.log("User authenticated via redirect:", user.email);
          // The auth state listener will set the user too, but this helps with UX
          setCurrentUser(user);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
        // Don't leave the app in loading state if redirect fails
        setIsLoading(false);
      }
    }
    
    checkRedirectResult();
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    console.log("Setting up auth state change listener");
    
    const unsubscribe = onAuthChange((user) => {
      console.log(
        'Auth state changed:',
        user ? `User logged in: ${user.email}` : 'User logged out'
      );
      setCurrentUser(user);
      setIsLoading(false);

      if (user) {
        setUserMessageCount(0);
        localStorage.setItem('chatMessageCount', '0');
      }
    });
    
    // Add a safety timeout to prevent permanent loading state
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.warn('Auth loading timed out after 5 seconds');
        setIsLoading(false);
      }
    }, 5000);
    
    return () => {
      console.log("Cleaning up auth state listener");
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  // Debug function to bypass auth issues
  const bypassAuth = () => {
    console.log("Auth bypass activated");
    setIsLoading(false);
    // This keeps the user null but removes the loading state
  };

  // Increment message count for anonymous users
  const incrementMessageCount = () => {
    if (currentUser) return;
    const next = userMessageCount + 1;
    setUserMessageCount(next);
    localStorage.setItem('chatMessageCount', String(next));
    console.log(`Message count incremented to ${next}`);
  };

  // Reset message count
  const resetMessageCount = () => {
    setUserMessageCount(0);
    localStorage.setItem('chatMessageCount', '0');
    console.log('Message count reset to 0');
  };

  // Sign out
  const signOut = async () => {
    try {
      await logoutUser();
      console.log('User signed out successfully');
    } catch (err) {
      console.error('Error signing out:', err);
      throw err;
    }
  };

  const value = {
    currentUser,
    isLoading,
    userMessageCount,
    incrementMessageCount,
    resetMessageCount,
    signOut,
    bypassAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;